// The model and effort picker beside the composer, and Settings → Models.
// Namespace `model-picker`; owner: COORD picker design lead. Model names come
// from the generated registry labels, never from this file.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  triggerLabel: 'Model and effort: {model}, {effort}',
  title: 'Model and effort',
  modelHeading: 'Model',
  effortHeading: 'Effort',
  autoBody: 'Recommended for most work, kept up to date.',
  opusBody: 'Most capable, for complex analysis and long research.',
  sonnetBody: 'Quick and capable, for everyday work.',
  haikuBody: 'Fast and affordable, for simple tasks.',
  solBody: 'Careful reasoning from OpenAI. May run outside the EU.',
  otherBody: 'Another model your company allows.',
  costLower: 'Lower cost',
  costHigher: 'Higher cost',
  fast: 'Fast',
  balanced: 'Balanced',
  thorough: 'Thorough',
  max: 'Max',
  fastBody: 'Quick answers to simple questions.',
  balancedBody: 'Thinks as much as each question needs.',
  thoroughBody: 'Takes more time to reason and check its work.',
  maxBody: 'For long research. The slowest and most careful.',
  unsupported: 'Not available with {model}',
  maxNote: 'Max takes longer and uses more credit. Each run stops at {cap}.',
  companyNote: 'Your company chooses which models are available.',
  manage: 'Manage models',

  settingsTitle: 'Models',
  settingsIntro:
    'Choose the models and effort levels people at {company} can use, and how much one run may spend.',
  loading: 'Loading model settings…',
  loadError: 'We couldn’t load model settings. Check your connection and try again.',
  unavailable: 'Model settings aren’t available here yet.',
  retry: 'Try again',
  modelsHelp: 'People choose from these in any chat. New chats start with the default.',
  defaultBadge: 'Default',
  makeDefault: 'Make default',
  defaultLocked: 'Choose another default before turning this off.',
  effortsHelp: 'More effort gives more careful answers, and takes longer and costs more.',
  capHeading: 'Credit limit per run',
  capHelp: 'A run stops cleanly when it has used this many credits. Long research uses the most.',
  capField: 'Limit per run',
  capRange: 'From {min} to {max}.',
  capNow: 'Each run stops at {cap}.',
  capUnit: 'credits',
  capCredits: '{credits} credits',
  decision: 'Changes apply to new messages for everyone at {company}.',
  save: 'Save',
  saving: 'Saving…',
  saved: 'Saved',
  conflict: 'Someone changed this a moment ago. We’ve loaded the latest settings.',
  saveError: 'We couldn’t save this. Try again.',
  adminOnly: 'Only Owners and Admins can change this.',
};
export type ModelPickerCopyKey = keyof typeof en;

export const modelPickerCopy: NamespaceCatalog<ModelPickerCopyKey> = {
  en,
  bg: {
    triggerLabel: 'Модел и задълбоченост: {model}, {effort}',
    title: 'Модел и задълбоченост',
    modelHeading: 'Модел',
    effortHeading: 'Задълбоченост',
    autoBody: 'Препоръчва се за повечето задачи, винаги актуален.',
    opusBody: 'Най-способният, за сложни анализи и дълги проучвания.',
    sonnetBody: 'Бърз и способен, за ежедневна работа.',
    haikuBody: 'Бърз и достъпен, за прости задачи.',
    solBody: 'Внимателно разсъждение от OpenAI. Може да работи извън ЕС.',
    otherBody: 'Друг модел, разрешен от вашата компания.',
    costLower: 'По-ниска цена',
    costHigher: 'По-висока цена',
    fast: 'Бързо',
    balanced: 'Умерено',
    thorough: 'Подробно',
    max: 'Максимум',
    fastBody: 'Бързи отговори на прости въпроси.',
    balancedBody: 'Мисли толкова, колкото изисква всеки въпрос.',
    thoroughBody: 'Отделя повече време да разсъждава и да проверява работата си.',
    maxBody: 'За дълги проучвания. Най-бавното и най-внимателното.',
    unsupported: 'Не е налично с {model}',
    maxNote:
      'Максималната задълбоченост отнема повече време и използва повече кредит. Всяко изпълнение спира при {cap}.',
    companyNote: 'Вашата компания избира кои модели са налични.',
    manage: 'Управление на моделите',
    settingsTitle: 'Модели',
    settingsIntro:
      'Изберете моделите и нивата на задълбоченост, които хората в {company} могат да използват, и колко може да струва едно изпълнение.',
    loading: 'Зареждане на настройките на моделите…',
    loadError:
      'Не успяхме да заредим настройките на моделите. Проверете връзката си и опитайте отново.',
    unavailable: 'Настройките на моделите все още не са налични тук.',
    retry: 'Опитайте отново',
    modelsHelp:
      'Всеки може да избира от тях във всеки чат. Новите чатове започват с модела по подразбиране.',
    defaultBadge: 'По подразбиране',
    makeDefault: 'Задаване по подразбиране',
    defaultLocked: 'Изберете друга стойност по подразбиране, преди да изключите тази.',
    effortsHelp:
      'По-голямата задълбоченост дава по-внимателни отговори, но отнема повече време и струва повече.',
    capHeading: 'Лимит на кредити за изпълнение',
    capHelp:
      'Изпълнението спира чисто, когато използва толкова кредити. Най-много използват дългите проучвания.',
    capField: 'Лимит за изпълнение',
    capRange: 'От {min} до {max}.',
    capNow: 'Всяко изпълнение спира при {cap}.',
    capUnit: 'кредита',
    capCredits: '{credits} кредита',
    decision: 'Промените важат за новите съобщения на всички в {company}.',
    save: 'Запазване',
    saving: 'Запазване…',
    saved: 'Запазено',
    conflict: 'Някой току-що промени това. Заредихме най-новите настройки.',
    saveError: 'Не успяхме да запазим. Опитайте отново.',
    adminOnly: 'Само собствениците и администраторите могат да променят това.',
  },
  cs: {
    triggerLabel: 'Model a důkladnost: {model}, {effort}',
    title: 'Model a důkladnost',
    modelHeading: 'Model',
    effortHeading: 'Důkladnost',
    autoBody: 'Doporučeno pro většinu práce, vždy aktuální.',
    opusBody: 'Nejschopnější, pro složité analýzy a dlouhý výzkum.',
    sonnetBody: 'Rychlý a schopný, pro každodenní práci.',
    haikuBody: 'Rychlý a cenově dostupný, pro jednoduché úkoly.',
    solBody: 'Pečlivé uvažování od OpenAI. Může běžet mimo EU.',
    otherBody: 'Další model, který vaše firma povoluje.',
    costLower: 'Nižší cena',
    costHigher: 'Vyšší cena',
    fast: 'Rychle',
    balanced: 'Vyváženě',
    thorough: 'Důkladně',
    max: 'Maximálně',
    fastBody: 'Rychlé odpovědi na jednoduché otázky.',
    balancedBody: 'Přemýšlí tolik, kolik každá otázka potřebuje.',
    thoroughBody: 'Věnuje víc času uvažování a kontrole své práce.',
    maxBody: 'Pro dlouhý výzkum. Nejpomalejší a nejpečlivější.',
    unsupported: 'S modelem {model} není k dispozici',
    maxNote:
      'Maximální důkladnost trvá déle a spotřebuje víc kreditu. Každý běh se zastaví na {cap}.',
    companyNote: 'Vaše firma vybírá, které modely jsou k dispozici.',
    manage: 'Spravovat modely',
    settingsTitle: 'Modely',
    settingsIntro:
      'Vyberte modely a úrovně důkladnosti, které mohou lidé ve firmě {company} používat, a kolik smí stát jeden běh.',
    loading: 'Načítání nastavení modelů…',
    loadError: 'Nastavení modelů se nepodařilo načíst. Zkontrolujte připojení a zkuste to znovu.',
    unavailable: 'Nastavení modelů tu zatím není k dispozici.',
    retry: 'Zkusit znovu',
    modelsHelp: 'Každý z nich může vybírat v libovolném chatu. Nové chaty začínají s výchozím.',
    defaultBadge: 'Výchozí',
    makeDefault: 'Nastavit jako výchozí',
    defaultLocked: 'Než tuto možnost vypnete, zvolte jinou výchozí.',
    effortsHelp: 'Vyšší důkladnost přináší pečlivější odpovědi, ale trvá déle a stojí víc.',
    capHeading: 'Limit kreditů na jeden běh',
    capHelp:
      'Běh se čistě zastaví, když spotřebuje tolik kreditů. Nejvíc spotřebuje dlouhý výzkum.',
    capField: 'Limit na jeden běh',
    capRange: 'Od {min} do {max}.',
    capNow: 'Každý běh se zastaví na {cap}.',
    capUnit: 'kreditů',
    capCredits: '{credits} kreditů',
    decision: 'Změny platí pro nové zprávy všech lidí ve firmě {company}.',
    save: 'Uložit',
    saving: 'Ukládání…',
    saved: 'Uloženo',
    conflict: 'Někdo to právě změnil. Načetli jsme nejnovější nastavení.',
    saveError: 'Uložení se nezdařilo. Zkuste to znovu.',
    adminOnly: 'Měnit to mohou jen vlastníci a správci.',
  },
  da: {
    triggerLabel: 'Model og grundighed: {model}, {effort}',
    title: 'Model og grundighed',
    modelHeading: 'Model',
    effortHeading: 'Grundighed',
    autoBody: 'Anbefalet til det meste arbejde og holdt opdateret.',
    opusBody: 'Mest kompetent, til kompleks analyse og lang research.',
    sonnetBody: 'Hurtig og kompetent, til hverdagens arbejde.',
    haikuBody: 'Hurtig og prisvenlig, til enkle opgaver.',
    solBody: 'Omhyggelig ræsonnering fra OpenAI. Kan køre uden for EU.',
    otherBody: 'En anden model, som din virksomhed tillader.',
    costLower: 'Lavere pris',
    costHigher: 'Højere pris',
    fast: 'Hurtig',
    balanced: 'Balanceret',
    thorough: 'Grundig',
    max: 'Maks',
    fastBody: 'Hurtige svar på enkle spørgsmål.',
    balancedBody: 'Tænker så meget, som hvert spørgsmål kræver.',
    thoroughBody: 'Bruger mere tid på at ræsonnere og tjekke sit arbejde.',
    maxBody: 'Til lang research. Den langsomste og mest omhyggelige.',
    unsupported: 'Ikke tilgængelig med {model}',
    maxNote: 'Maks tager længere tid og bruger mere kredit. Hver kørsel stopper ved {cap}.',
    companyNote: 'Din virksomhed vælger, hvilke modeller der er tilgængelige.',
    manage: 'Administrer modeller',
    settingsTitle: 'Modeller',
    settingsIntro:
      'Vælg de modeller og niveauer af grundighed, som folk hos {company} kan bruge, og hvor meget én kørsel må koste.',
    loading: 'Indlæser modelindstillinger…',
    loadError: 'Vi kunne ikke indlæse modelindstillingerne. Tjek din forbindelse, og prøv igen.',
    unavailable: 'Modelindstillinger er ikke tilgængelige her endnu.',
    retry: 'Prøv igen',
    modelsHelp: 'Alle kan vælge mellem disse i enhver chat. Nye chats starter med standarden.',
    defaultBadge: 'Standard',
    makeDefault: 'Gør til standard',
    defaultLocked: 'Vælg en anden standard, før du slår denne fra.',
    effortsHelp:
      'Mere grundighed giver mere omhyggelige svar, men tager længere tid og koster mere.',
    capHeading: 'Kreditgrænse pr. kørsel',
    capHelp:
      'En kørsel stopper pænt, når den har brugt så mange kreditter. Lang research bruger mest.',
    capField: 'Grænse pr. kørsel',
    capRange: 'Fra {min} til {max}.',
    capNow: 'Hver kørsel stopper ved {cap}.',
    capUnit: 'kreditter',
    capCredits: '{credits} kreditter',
    decision: 'Ændringer gælder nye beskeder for alle hos {company}.',
    save: 'Gem',
    saving: 'Gemmer…',
    saved: 'Gemt',
    conflict: 'Nogen har lige ændret dette. Vi har indlæst de nyeste indstillinger.',
    saveError: 'Vi kunne ikke gemme dette. Prøv igen.',
    adminOnly: 'Kun ejere og administratorer kan ændre dette.',
  },
  de: {
    triggerLabel: 'Modell und Denktiefe: {model}, {effort}',
    title: 'Modell und Denktiefe',
    modelHeading: 'Modell',
    effortHeading: 'Denktiefe',
    autoBody: 'Empfohlen für die meisten Aufgaben, immer aktuell.',
    opusBody: 'Am leistungsfähigsten, für komplexe Analysen und lange Recherchen.',
    sonnetBody: 'Schnell und leistungsfähig, für die tägliche Arbeit.',
    haikuBody: 'Schnell und günstig, für einfache Aufgaben.',
    solBody: 'Sorgfältiges Schlussfolgern von OpenAI. Kann außerhalb der EU laufen.',
    otherBody: 'Ein weiteres Modell, das Ihr Unternehmen erlaubt.',
    costLower: 'Günstiger',
    costHigher: 'Teurer',
    fast: 'Schnell',
    balanced: 'Ausgewogen',
    thorough: 'Gründlich',
    max: 'Max',
    fastBody: 'Schnelle Antworten auf einfache Fragen.',
    balancedBody: 'Denkt so viel nach, wie jede Frage braucht.',
    thoroughBody: 'Nimmt sich mehr Zeit zum Nachdenken und Prüfen.',
    maxBody: 'Für lange Recherchen. Am langsamsten und gründlichsten.',
    unsupported: 'Mit {model} nicht verfügbar',
    maxNote: 'Max dauert länger und verbraucht mehr Guthaben. Jeder Lauf endet bei {cap}.',
    companyNote: 'Ihr Unternehmen legt fest, welche Modelle verfügbar sind.',
    manage: 'Modelle verwalten',
    settingsTitle: 'Modelle',
    settingsIntro:
      'Legen Sie fest, welche Modelle und Denktiefen die Personen bei {company} nutzen können und wie viel ein Lauf kosten darf.',
    loading: 'Modelleinstellungen werden geladen…',
    loadError:
      'Die Modelleinstellungen konnten nicht geladen werden. Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    unavailable: 'Modelleinstellungen sind hier noch nicht verfügbar.',
    retry: 'Erneut versuchen',
    modelsHelp: 'Diese stehen in jedem Chat zur Auswahl. Neue Chats beginnen mit dem Standard.',
    defaultBadge: 'Standard',
    makeDefault: 'Als Standard festlegen',
    defaultLocked: 'Legen Sie zuerst einen anderen Standard fest, bevor Sie diesen ausschalten.',
    effortsHelp:
      'Mehr Denktiefe ergibt sorgfältigere Antworten, dauert aber länger und kostet mehr.',
    capHeading: 'Credit-Limit pro Lauf',
    capHelp:
      'Ein Lauf endet sauber, wenn er so viele Credits verbraucht hat. Lange Recherchen verbrauchen am meisten.',
    capField: 'Limit pro Lauf',
    capRange: 'Von {min} bis {max}.',
    capNow: 'Jeder Lauf endet bei {cap}.',
    capUnit: 'Credits',
    capCredits: '{credits} Credits',
    decision: 'Änderungen gelten für neue Nachrichten aller Personen bei {company}.',
    save: 'Speichern',
    saving: 'Wird gespeichert…',
    saved: 'Gespeichert',
    conflict: 'Jemand hat dies gerade geändert. Wir haben die neuesten Einstellungen geladen.',
    saveError: 'Das konnte nicht gespeichert werden. Versuchen Sie es erneut.',
    adminOnly: 'Nur Inhaber und Admins können dies ändern.',
  },
  el: {
    triggerLabel: 'Μοντέλο και εμβάθυνση: {model}, {effort}',
    title: 'Μοντέλο και εμβάθυνση',
    modelHeading: 'Μοντέλο',
    effortHeading: 'Εμβάθυνση',
    autoBody: 'Προτείνεται για τις περισσότερες εργασίες, πάντα ενημερωμένο.',
    opusBody: 'Το πιο ικανό, για σύνθετες αναλύσεις και μεγάλη έρευνα.',
    sonnetBody: 'Γρήγορο και ικανό, για την καθημερινή δουλειά.',
    haikuBody: 'Γρήγορο και οικονομικό, για απλές εργασίες.',
    solBody: 'Προσεκτική συλλογιστική από την OpenAI. Μπορεί να εκτελείται εκτός ΕΕ.',
    otherBody: 'Άλλο μοντέλο που επιτρέπει η εταιρεία σας.',
    costLower: 'Χαμηλότερο κόστος',
    costHigher: 'Υψηλότερο κόστος',
    fast: 'Γρήγορα',
    balanced: 'Μέτρια',
    thorough: 'Διεξοδικά',
    max: 'Μέγιστα',
    fastBody: 'Γρήγορες απαντήσεις σε απλές ερωτήσεις.',
    balancedBody: 'Σκέφτεται όσο χρειάζεται κάθε ερώτηση.',
    thoroughBody: 'Αφιερώνει περισσότερο χρόνο για να συλλογιστεί και να ελέγξει τη δουλειά του.',
    maxBody: 'Για μεγάλη έρευνα. Το πιο αργό και το πιο προσεκτικό.',
    unsupported: 'Δεν είναι διαθέσιμο με το {model}',
    maxNote:
      'Η μέγιστη εμβάθυνση διαρκεί περισσότερο και χρησιμοποιεί περισσότερη πίστωση. Κάθε εκτέλεση σταματά στα {cap}.',
    companyNote: 'Η εταιρεία σας επιλέγει ποια μοντέλα είναι διαθέσιμα.',
    manage: 'Διαχείριση μοντέλων',
    settingsTitle: 'Μοντέλα',
    settingsIntro:
      'Επιλέξτε τα μοντέλα και τα επίπεδα εμβάθυνσης που μπορούν να χρησιμοποιούν τα άτομα της {company}, και πόσο μπορεί να κοστίσει μία εκτέλεση.',
    loading: 'Φόρτωση ρυθμίσεων μοντέλων…',
    loadError:
      'Δεν ήταν δυνατή η φόρτωση των ρυθμίσεων μοντέλων. Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.',
    unavailable: 'Οι ρυθμίσεις μοντέλων δεν είναι ακόμη διαθέσιμες εδώ.',
    retry: 'Δοκιμάστε ξανά',
    modelsHelp:
      'Όλοι μπορούν να επιλέγουν από αυτά σε οποιαδήποτε συνομιλία. Οι νέες συνομιλίες ξεκινούν με την προεπιλογή.',
    defaultBadge: 'Προεπιλογή',
    makeDefault: 'Ορισμός ως προεπιλογή',
    defaultLocked: 'Επιλέξτε άλλη προεπιλογή πριν απενεργοποιήσετε αυτή.',
    effortsHelp:
      'Περισσότερη εμβάθυνση δίνει πιο προσεκτικές απαντήσεις, αλλά διαρκεί περισσότερο και κοστίζει περισσότερο.',
    capHeading: 'Όριο πιστώσεων ανά εκτέλεση',
    capHelp:
      'Μια εκτέλεση σταματά ομαλά όταν χρησιμοποιήσει τόσες πιστώσεις. Η μακροσκελής έρευνα χρησιμοποιεί τις περισσότερες.',
    capField: 'Όριο ανά εκτέλεση',
    capRange: 'Από {min} έως {max}.',
    capNow: 'Κάθε εκτέλεση σταματά στα {cap}.',
    capUnit: 'πιστώσεις',
    capCredits: '{credits} πιστώσεις',
    decision: 'Οι αλλαγές ισχύουν για τα νέα μηνύματα όλων στην {company}.',
    save: 'Αποθήκευση',
    saving: 'Αποθήκευση…',
    saved: 'Αποθηκεύτηκε',
    conflict: 'Κάποιος μόλις το άλλαξε. Φορτώσαμε τις πιο πρόσφατες ρυθμίσεις.',
    saveError: 'Η αποθήκευση απέτυχε. Δοκιμάστε ξανά.',
    adminOnly: 'Μόνο οι κάτοχοι και οι διαχειριστές μπορούν να το αλλάξουν.',
  },
  es: {
    triggerLabel: 'Modelo y razonamiento: {model}, {effort}',
    title: 'Modelo y razonamiento',
    modelHeading: 'Modelo',
    effortHeading: 'Razonamiento',
    autoBody: 'Recomendado para la mayoría del trabajo, siempre actualizado.',
    opusBody: 'El más capaz, para análisis complejos e investigaciones largas.',
    sonnetBody: 'Rápido y capaz, para el trabajo diario.',
    haikuBody: 'Rápido y económico, para tareas sencillas.',
    solBody: 'Razonamiento cuidadoso de OpenAI. Puede ejecutarse fuera de la UE.',
    otherBody: 'Otro modelo que tu empresa permite.',
    costLower: 'Menor coste',
    costHigher: 'Mayor coste',
    fast: 'Rápido',
    balanced: 'Equilibrado',
    thorough: 'A fondo',
    max: 'Máximo',
    fastBody: 'Respuestas rápidas a preguntas sencillas.',
    balancedBody: 'Piensa tanto como necesita cada pregunta.',
    thoroughBody: 'Dedica más tiempo a razonar y revisar su trabajo.',
    maxBody: 'Para investigaciones largas. El más lento y minucioso.',
    unsupported: 'No disponible con {model}',
    maxNote: 'Máximo tarda más y consume más crédito. Cada ejecución se detiene en {cap}.',
    companyNote: 'Tu empresa elige qué modelos están disponibles.',
    manage: 'Gestionar modelos',
    settingsTitle: 'Modelos',
    settingsIntro:
      'Elige los modelos y niveles de razonamiento que pueden usar las personas de {company}, y cuánto puede gastar una ejecución.',
    loading: 'Cargando la configuración de modelos…',
    loadError:
      'No hemos podido cargar la configuración de modelos. Comprueba tu conexión e inténtalo de nuevo.',
    unavailable: 'La configuración de modelos aún no está disponible aquí.',
    retry: 'Intentar de nuevo',
    modelsHelp:
      'Todos eligen entre estos en cualquier chat. Los chats nuevos empiezan con el predeterminado.',
    defaultBadge: 'Predeterminado',
    makeDefault: 'Usar como predeterminado',
    defaultLocked: 'Elige otro predeterminado antes de desactivar este.',
    effortsHelp: 'Más razonamiento da respuestas más cuidadosas, pero tarda más y cuesta más.',
    capHeading: 'Límite de créditos por ejecución',
    capHelp:
      'Una ejecución se detiene limpiamente cuando ha usado esta cantidad de créditos. Las investigaciones largas son las que más usan.',
    capField: 'Límite por ejecución',
    capRange: 'De {min} a {max}.',
    capNow: 'Cada ejecución se detiene en {cap}.',
    capUnit: 'créditos',
    capCredits: '{credits} créditos',
    decision: 'Los cambios se aplican a los mensajes nuevos de todas las personas de {company}.',
    save: 'Guardar',
    saving: 'Guardando…',
    saved: 'Guardado',
    conflict: 'Alguien acaba de cambiar esto. Hemos cargado la configuración más reciente.',
    saveError: 'No hemos podido guardarlo. Inténtalo de nuevo.',
    adminOnly: 'Solo los propietarios y administradores pueden cambiar esto.',
  },
  et: {
    triggerLabel: 'Mudel ja põhjalikkus: {model}, {effort}',
    title: 'Mudel ja põhjalikkus',
    modelHeading: 'Mudel',
    effortHeading: 'Põhjalikkus',
    autoBody: 'Soovitatud enamikuks tööks, alati ajakohane.',
    opusBody: 'Kõige võimekam, keerukaks analüüsiks ja pikaks uurimistööks.',
    sonnetBody: 'Kiire ja võimekas, igapäevatööks.',
    haikuBody: 'Kiire ja soodne, lihtsate ülesannete jaoks.',
    solBody: 'Hoolikas arutlus OpenAI-lt. Võib töötada väljaspool ELi.',
    otherBody: 'Veel üks mudel, mille sinu ettevõte lubab.',
    costLower: 'Odavam',
    costHigher: 'Kallim',
    fast: 'Kiire',
    balanced: 'Tasakaalus',
    thorough: 'Põhjalik',
    max: 'Maksimum',
    fastBody: 'Kiired vastused lihtsatele küsimustele.',
    balancedBody: 'Mõtleb nii palju, kui iga küsimus vajab.',
    thoroughBody: 'Võtab rohkem aega arutlemiseks ja oma töö kontrollimiseks.',
    maxBody: 'Pikaks uurimistööks. Kõige aeglasem ja hoolikam.',
    unsupported: 'Mudeliga {model} pole saadaval',
    maxNote:
      'Maksimum võtab kauem aega ja kasutab rohkem krediiti. Iga käivitus peatub summal {cap}.',
    companyNote: 'Sinu ettevõte valib, millised mudelid on saadaval.',
    manage: 'Halda mudeleid',
    settingsTitle: 'Mudelid',
    settingsIntro:
      'Vali, milliseid mudeleid ja põhjalikkuse tasemeid saavad ettevõtte {company} inimesed kasutada ning kui palju võib üks käivitus maksta.',
    loading: 'Mudeli seadete laadimine…',
    loadError: 'Mudeli seadeid ei õnnestunud laadida. Kontrolli ühendust ja proovi uuesti.',
    unavailable: 'Mudeli seaded pole siin veel saadaval.',
    retry: 'Proovi uuesti',
    modelsHelp: 'Kõik saavad neist valida igas vestluses. Uued vestlused algavad vaikevalikuga.',
    defaultBadge: 'Vaikimisi',
    makeDefault: 'Määra vaikimisi',
    defaultLocked: 'Vali enne selle väljalülitamist teine vaikevalik.',
    effortsHelp:
      'Suurem põhjalikkus annab hoolikamad vastused, kuid võtab kauem aega ja maksab rohkem.',
    capHeading: 'Krediidipiirang käivituse kohta',
    capHelp:
      'Käivitus peatub korrektselt, kui see on kasutanud nii palju krediiti. Pikk uurimistöö kasutab kõige rohkem.',
    capField: 'Piirang käivituse kohta',
    capRange: 'Vahemikus {min} kuni {max}.',
    capNow: 'Iga käivitus peatub summal {cap}.',
    capUnit: 'krediiti',
    capCredits: '{credits} krediiti',
    decision: 'Muudatused kehtivad kõigi ettevõtte {company} inimeste uutele sõnumitele.',
    save: 'Salvesta',
    saving: 'Salvestamine…',
    saved: 'Salvestatud',
    conflict: 'Keegi muutis seda just praegu. Laadisime uusimad seaded.',
    saveError: 'Salvestamine ebaõnnestus. Proovi uuesti.',
    adminOnly: 'Seda saavad muuta ainult omanikud ja administraatorid.',
  },
  fi: {
    triggerLabel: 'Malli ja perusteellisuus: {model}, {effort}',
    title: 'Malli ja perusteellisuus',
    modelHeading: 'Malli',
    effortHeading: 'Perusteellisuus',
    autoBody: 'Suositeltu useimpiin töihin, pidetään ajan tasalla.',
    opusBody: 'Kyvykkäin, monimutkaiseen analyysiin ja pitkään tutkimukseen.',
    sonnetBody: 'Nopea ja kyvykäs, arkiseen työhön.',
    haikuBody: 'Nopea ja edullinen, yksinkertaisiin tehtäviin.',
    solBody: 'Huolellista päättelyä OpenAI:lta. Voi toimia EU:n ulkopuolella.',
    otherBody: 'Toinen malli, jonka organisaatiosi sallii.',
    costLower: 'Edullisempi',
    costHigher: 'Kalliimpi',
    fast: 'Nopea',
    balanced: 'Normaali',
    thorough: 'Tarkka',
    max: 'Maksimi',
    fastBody: 'Nopeat vastaukset yksinkertaisiin kysymyksiin.',
    balancedBody: 'Pohtii niin paljon kuin kukin kysymys vaatii.',
    thoroughBody: 'Käyttää enemmän aikaa päättelyyn ja työnsä tarkistamiseen.',
    maxBody: 'Pitkään tutkimukseen. Hitain ja huolellisin.',
    unsupported: 'Ei käytettävissä mallilla {model}',
    maxNote:
      'Maksimi kestää kauemmin ja kuluttaa enemmän krediittiä. Jokainen ajo pysähtyy rajaan {cap}.',
    companyNote: 'Organisaatiosi päättää, mitkä mallit ovat käytettävissä.',
    manage: 'Hallitse malleja',
    settingsTitle: 'Mallit',
    settingsIntro:
      'Valitse, mitä malleja ja perusteellisuuden tasoja organisaation {company} käyttäjät voivat käyttää ja paljonko yksi ajo saa maksaa.',
    loading: 'Ladataan malliasetuksia…',
    loadError: 'Malliasetuksia ei voitu ladata. Tarkista yhteys ja yritä uudelleen.',
    unavailable: 'Malliasetukset eivät ole vielä käytettävissä täällä.',
    retry: 'Yritä uudelleen',
    modelsHelp:
      'Kaikki voivat valita näistä missä tahansa keskustelussa. Uudet keskustelut alkavat oletuksella.',
    defaultBadge: 'Oletus',
    makeDefault: 'Aseta oletukseksi',
    defaultLocked: 'Valitse toinen oletus ennen kuin poistat tämän käytöstä.',
    effortsHelp:
      'Suurempi perusteellisuus antaa huolellisempia vastauksia, mutta kestää kauemmin ja maksaa enemmän.',
    capHeading: 'Krediittiraja ajoa kohden',
    capHelp:
      'Ajo pysähtyy hallitusti, kun se on käyttänyt näin monta krediittiä. Pitkä tutkimus käyttää eniten.',
    capField: 'Raja ajoa kohden',
    capRange: 'Väliltä {min}–{max}.',
    capNow: 'Jokainen ajo pysähtyy rajaan {cap}.',
    capUnit: 'krediittiä',
    capCredits: '{credits} krediittiä',
    decision: 'Muutokset koskevat kaikkien organisaation {company} käyttäjien uusia viestejä.',
    save: 'Tallenna',
    saving: 'Tallennetaan…',
    saved: 'Tallennettu',
    conflict: 'Joku muutti tätä juuri. Latasimme uusimmat asetukset.',
    saveError: 'Tallentaminen epäonnistui. Yritä uudelleen.',
    adminOnly: 'Vain omistajat ja ylläpitäjät voivat muuttaa tätä.',
  },
  fr: {
    triggerLabel: 'Modèle et réflexion : {model}, {effort}',
    title: 'Modèle et réflexion',
    modelHeading: 'Modèle',
    effortHeading: 'Réflexion',
    autoBody: 'Recommandé pour la plupart des tâches, toujours à jour.',
    opusBody: 'Le plus performant, pour les analyses complexes et les longues recherches.',
    sonnetBody: 'Rapide et performant, pour le travail quotidien.',
    haikuBody: 'Rapide et économique, pour les tâches simples.',
    solBody: 'Raisonnement soigné d’OpenAI. Peut s’exécuter hors de l’UE.',
    otherBody: 'Un autre modèle autorisé par votre entreprise.',
    costLower: 'Coût réduit',
    costHigher: 'Coût plus élevé',
    fast: 'Rapide',
    balanced: 'Équilibrée',
    thorough: 'Approfondie',
    max: 'Maximale',
    fastBody: 'Des réponses rapides aux questions simples.',
    balancedBody: 'Réfléchit autant que chaque question l’exige.',
    thoroughBody: 'Prend plus de temps pour raisonner et vérifier son travail.',
    maxBody: 'Pour les longues recherches. La plus lente et la plus minutieuse.',
    unsupported: 'Non disponible avec {model}',
    maxNote:
      'La réflexion maximale prend plus de temps et consomme plus de crédit. Chaque exécution s’arrête à {cap}.',
    companyNote: 'Votre entreprise choisit les modèles disponibles.',
    manage: 'Gérer les modèles',
    settingsTitle: 'Modèles',
    settingsIntro:
      'Choisissez les modèles et les niveaux de réflexion que les personnes de {company} peuvent utiliser, et le montant maximal d’une exécution.',
    loading: 'Chargement des paramètres des modèles…',
    loadError:
      'Impossible de charger les paramètres des modèles. Vérifiez votre connexion et réessayez.',
    unavailable: 'Les paramètres des modèles ne sont pas encore disponibles ici.',
    retry: 'Réessayer',
    modelsHelp:
      'Chacun choisit parmi ces modèles dans n’importe quelle conversation. Les nouvelles conversations commencent avec le modèle par défaut.',
    defaultBadge: 'Par défaut',
    makeDefault: 'Définir par défaut',
    defaultLocked: 'Choisissez une autre valeur par défaut avant de désactiver celle-ci.',
    effortsHelp:
      'Plus de réflexion donne des réponses plus soignées, mais prend plus de temps et coûte plus cher.',
    capHeading: 'Limite de crédits par exécution',
    capHelp:
      'Une exécution s’arrête proprement lorsqu’elle a utilisé ce nombre de crédits. Les longues recherches en utilisent le plus.',
    capField: 'Limite par exécution',
    capRange: 'De {min} à {max}.',
    capNow: 'Chaque exécution s’arrête à {cap}.',
    capUnit: 'crédits',
    capCredits: '{credits} crédits',
    decision:
      'Les modifications s’appliquent aux nouveaux messages de toutes les personnes de {company}.',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    saved: 'Enregistré',
    conflict: 'Quelqu’un vient de modifier ce réglage. Nous avons chargé les derniers paramètres.',
    saveError: 'Impossible d’enregistrer. Réessayez.',
    adminOnly: 'Seuls les propriétaires et les administrateurs peuvent modifier ce réglage.',
  },
  ga: {
    triggerLabel: 'Samhail agus mionchúram: {model}, {effort}',
    title: 'Samhail agus mionchúram',
    modelHeading: 'Samhail',
    effortHeading: 'Mionchúram',
    autoBody: 'Molta don chuid is mó den obair, coinnithe cothrom le dáta.',
    opusBody: 'An ceann is cumasaí, d’anailís chasta agus do thaighde fada.',
    sonnetBody: 'Tapa agus cumasach, don obair laethúil.',
    haikuBody: 'Tapa agus inacmhainne, le haghaidh tascanna simplí.',
    solBody: 'Réasúnaíocht chúramach ó OpenAI. D’fhéadfadh sé rith lasmuigh den AE.',
    otherBody: 'Samhail eile a cheadaíonn do chuideachta.',
    costLower: 'Costas níos ísle',
    costHigher: 'Costas níos airde',
    fast: 'Tapa',
    balanced: 'Cothrom',
    thorough: 'Críochnúil',
    max: 'Uasta',
    fastBody: 'Freagraí tapa ar cheisteanna simplí.',
    balancedBody: 'Smaoiníonn sé an méid a theastaíonn ó gach ceist.',
    thoroughBody: 'Tógann sé níos mó ama réasúnú agus a chuid oibre a sheiceáil.',
    maxBody: 'Do thaighde fada. An ceann is moille agus is cúramaí.',
    unsupported: 'Níl sé ar fáil le {model}',
    maxNote:
      'Tógann Uasta níos faide agus úsáideann sé níos mó creidmheasa. Stopann gach rith ag {cap}.',
    companyNote: 'Roghnaíonn do chuideachta na samhlacha atá ar fáil.',
    manage: 'Bainistigh samhlacha',
    settingsTitle: 'Samhlacha',
    settingsIntro:
      'Roghnaigh na samhlacha agus na leibhéil mhionchúraim is féidir le daoine ag {company} a úsáid, agus an méid is féidir le rith amháin a chaitheamh.',
    loading: 'Socruithe samhlacha á lódáil…',
    loadError:
      'Níorbh fhéidir na socruithe samhlacha a lódáil. Seiceáil do cheangal agus bain triail eile as.',
    unavailable: 'Níl socruithe samhlacha ar fáil anseo fós.',
    retry: 'Bain triail eile as',
    modelsHelp:
      'Is féidir le gach duine rogha a dhéanamh astu seo in aon chomhrá. Tosaíonn comhráite nua leis an réamhshocrú.',
    defaultBadge: 'Réamhshocrú',
    makeDefault: 'Déan réamhshocrú de',
    defaultLocked: 'Roghnaigh réamhshocrú eile sula múchann tú é seo.',
    effortsHelp:
      'Tugann níos mó mionchúraim freagraí níos cúramaí, ach tógann sé níos faide agus cosnaíonn sé níos mó.',
    capHeading: 'Teorainn chreidmheasa in aghaidh an rith',
    capHelp:
      'Stopann rith go slachtmhar nuair a bhíonn an méid sin creidmheasa úsáidte aige. Is é taighde fada a úsáideann an méid is mó.',
    capField: 'Teorainn in aghaidh an rith',
    capRange: 'Ó {min} go {max}.',
    capNow: 'Stopann gach rith ag {cap}.',
    capUnit: 'creidmheas',
    capCredits: '{credits} creidmheas',
    decision: 'Baineann na hathruithe le teachtaireachtaí nua gach duine ag {company}.',
    save: 'Sábháil',
    saving: 'Á shábháil…',
    saved: 'Sábháilte',
    conflict: 'D’athraigh duine éigin é seo díreach anois. Lódálamar na socruithe is déanaí.',
    saveError: 'Níorbh fhéidir é seo a shábháil. Bain triail eile as.',
    adminOnly: 'Ní féidir ach le húinéirí agus riarthóirí é seo a athrú.',
  },
  hr: {
    triggerLabel: 'Model i temeljitost: {model}, {effort}',
    title: 'Model i temeljitost',
    modelHeading: 'Model',
    effortHeading: 'Temeljitost',
    autoBody: 'Preporučeno za većinu posla, uvijek ažurno.',
    opusBody: 'Najsposobniji, za složene analize i dugo istraživanje.',
    sonnetBody: 'Brz i sposoban, za svakodnevni rad.',
    haikuBody: 'Brz i povoljan, za jednostavne zadatke.',
    solBody: 'Pažljivo zaključivanje tvrtke OpenAI. Može raditi izvan EU-a.',
    otherBody: 'Drugi model koji vaša tvrtka dopušta.',
    costLower: 'Niža cijena',
    costHigher: 'Viša cijena',
    fast: 'Brzo',
    balanced: 'Umjereno',
    thorough: 'Temeljito',
    max: 'Najviše',
    fastBody: 'Brzi odgovori na jednostavna pitanja.',
    balancedBody: 'Razmišlja onoliko koliko svako pitanje zahtijeva.',
    thoroughBody: 'Uzima više vremena za zaključivanje i provjeru svog rada.',
    maxBody: 'Za dugo istraživanje. Najsporije i najpažljivije.',
    unsupported: 'Nije dostupno s modelom {model}',
    maxNote:
      'Najviša temeljitost traje dulje i troši više kredita. Svako se pokretanje zaustavlja na {cap}.',
    companyNote: 'Vaša tvrtka bira koji su modeli dostupni.',
    manage: 'Upravljaj modelima',
    settingsTitle: 'Modeli',
    settingsIntro:
      'Odaberite modele i razine temeljitosti koje ljudi u tvrtki {company} mogu koristiti te koliko smije stajati jedno pokretanje.',
    loading: 'Učitavanje postavki modela…',
    loadError: 'Postavke modela nije bilo moguće učitati. Provjerite vezu i pokušajte ponovno.',
    unavailable: 'Postavke modela ovdje još nisu dostupne.',
    retry: 'Pokušaj ponovno',
    modelsHelp:
      'Svatko može birati među njima u bilo kojem razgovoru. Novi razgovori počinju sa zadanim.',
    defaultBadge: 'Zadano',
    makeDefault: 'Postavi kao zadano',
    defaultLocked: 'Prije isključivanja odaberite drugu zadanu mogućnost.',
    effortsHelp: 'Veća temeljitost daje pažljivije odgovore, ali traje dulje i košta više.',
    capHeading: 'Ograničenje kredita po pokretanju',
    capHelp:
      'Pokretanje se uredno zaustavlja kada potroši toliko kredita. Najviše troši dugo istraživanje.',
    capField: 'Ograničenje po pokretanju',
    capRange: 'Od {min} do {max}.',
    capNow: 'Svako se pokretanje zaustavlja na {cap}.',
    capUnit: 'kredita',
    capCredits: '{credits} kredita',
    decision: 'Promjene vrijede za nove poruke svih ljudi u tvrtki {company}.',
    save: 'Spremi',
    saving: 'Spremanje…',
    saved: 'Spremljeno',
    conflict: 'Netko je ovo upravo promijenio. Učitali smo najnovije postavke.',
    saveError: 'Spremanje nije uspjelo. Pokušajte ponovno.',
    adminOnly: 'Ovo mogu mijenjati samo vlasnici i administratori.',
  },
  hu: {
    triggerLabel: 'Modell és alaposság: {model}, {effort}',
    title: 'Modell és alaposság',
    modelHeading: 'Modell',
    effortHeading: 'Alaposság',
    autoBody: 'A legtöbb munkához ajánlott, mindig naprakész.',
    opusBody: 'A legképesebb, összetett elemzéshez és hosszú kutatáshoz.',
    sonnetBody: 'Gyors és képes, a mindennapi munkához.',
    haikuBody: 'Gyors és kedvező árú, egyszerű feladatokhoz.',
    solBody: 'Gondos érvelés az OpenAI-tól. Az EU-n kívül is futhat.',
    otherBody: 'Egy másik modell, amelyet a cége engedélyez.',
    costLower: 'Alacsonyabb költség',
    costHigher: 'Magasabb költség',
    fast: 'Gyors',
    balanced: 'Normál',
    thorough: 'Alapos',
    max: 'Maximális',
    fastBody: 'Gyors válaszok egyszerű kérdésekre.',
    balancedBody: 'Annyit gondolkodik, amennyit minden kérdés igényel.',
    thoroughBody: 'Több időt szán az érvelésre és a munkája ellenőrzésére.',
    maxBody: 'Hosszú kutatáshoz. A leglassabb és a legalaposabb.',
    unsupported: 'Nem érhető el ezzel: {model}',
    maxNote:
      'A maximális alaposság tovább tart és több kreditet használ. Minden futtatás {cap} összegnél leáll.',
    companyNote: 'A cége választja ki, mely modellek érhetők el.',
    manage: 'Modellek kezelése',
    settingsTitle: 'Modellek',
    settingsIntro:
      'Válassza ki, mely modelleket és alapossági szinteket használhatják a(z) {company} munkatársai, és mennyibe kerülhet egy futtatás.',
    loading: 'Modellbeállítások betöltése…',
    loadError:
      'Nem sikerült betölteni a modellbeállításokat. Ellenőrizze a kapcsolatot, és próbálja újra.',
    unavailable: 'A modellbeállítások itt még nem érhetők el.',
    retry: 'Újrapróbálás',
    modelsHelp:
      'Bármelyik csevegésben választhatók. Az új csevegések az alapértelmezettel indulnak.',
    defaultBadge: 'Alapértelmezett',
    makeDefault: 'Legyen alapértelmezett',
    defaultLocked: 'Kikapcsolás előtt válasszon másik alapértelmezettet.',
    effortsHelp: 'A nagyobb alaposság gondosabb válaszokat ad, de tovább tart és többe kerül.',
    capHeading: 'Kreditkeret futtatásonként',
    capHelp:
      'A futtatás rendben leáll, amikor ennyi kreditet felhasznált. A hosszú kutatás használja a legtöbbet.',
    capField: 'Korlát futtatásonként',
    capRange: '{min} és {max} között.',
    capNow: 'Minden futtatás {cap} összegnél leáll.',
    capUnit: 'kredit',
    capCredits: '{credits} kredit',
    decision: 'A módosítások a(z) {company} minden munkatársának új üzeneteire vonatkoznak.',
    save: 'Mentés',
    saving: 'Mentés…',
    saved: 'Mentve',
    conflict: 'Valaki épp most módosította ezt. Betöltöttük a legfrissebb beállításokat.',
    saveError: 'Nem sikerült menteni. Próbálja újra.',
    adminOnly: 'Ezt csak a tulajdonosok és a rendszergazdák módosíthatják.',
  },
  it: {
    triggerLabel: 'Modello e ragionamento: {model}, {effort}',
    title: 'Modello e ragionamento',
    modelHeading: 'Modello',
    effortHeading: 'Ragionamento',
    autoBody: 'Consigliato per la maggior parte del lavoro, sempre aggiornato.',
    opusBody: 'Il più capace, per analisi complesse e ricerche lunghe.',
    sonnetBody: 'Rapido e capace, per il lavoro di tutti i giorni.',
    haikuBody: 'Veloce ed economico, per attività semplici.',
    solBody: 'Ragionamento accurato di OpenAI. Può essere eseguito fuori dall’UE.',
    otherBody: 'Un altro modello consentito dalla tua azienda.',
    costLower: 'Costo minore',
    costHigher: 'Costo maggiore',
    fast: 'Rapido',
    balanced: 'Bilanciato',
    thorough: 'Accurato',
    max: 'Massimo',
    fastBody: 'Risposte rapide a domande semplici.',
    balancedBody: 'Ragiona quanto serve per ogni domanda.',
    thoroughBody: 'Si prende più tempo per ragionare e verificare il lavoro.',
    maxBody: 'Per ricerche lunghe. Il più lento e il più accurato.',
    unsupported: 'Non disponibile con {model}',
    maxNote: 'Massimo richiede più tempo e usa più credito. Ogni esecuzione si ferma a {cap}.',
    companyNote: 'La tua azienda sceglie quali modelli sono disponibili.',
    manage: 'Gestisci modelli',
    settingsTitle: 'Modelli',
    settingsIntro:
      'Scegli i modelli e i livelli di ragionamento che le persone di {company} possono usare e quanto può costare una singola esecuzione.',
    loading: 'Caricamento delle impostazioni dei modelli…',
    loadError:
      'Non è stato possibile caricare le impostazioni dei modelli. Controlla la connessione e riprova.',
    unavailable: 'Le impostazioni dei modelli non sono ancora disponibili qui.',
    retry: 'Riprova',
    modelsHelp:
      'Tutti possono scegliere tra questi in qualsiasi chat. Le nuove chat iniziano con il predefinito.',
    defaultBadge: 'Predefinito',
    makeDefault: 'Imposta come predefinito',
    defaultLocked: 'Scegli un altro predefinito prima di disattivare questo.',
    effortsHelp: 'Più ragionamento dà risposte più accurate, ma richiede più tempo e costa di più.',
    capHeading: 'Limite di crediti per esecuzione',
    capHelp:
      'Un’esecuzione si ferma in modo ordinato quando ha usato questo numero di crediti. Le ricerche lunghe sono quelle che ne usano di più.',
    capField: 'Limite per esecuzione',
    capRange: 'Da {min} a {max}.',
    capNow: 'Ogni esecuzione si ferma a {cap}.',
    capUnit: 'crediti',
    capCredits: '{credits} crediti',
    decision: 'Le modifiche valgono per i nuovi messaggi di tutte le persone di {company}.',
    save: 'Salva',
    saving: 'Salvataggio…',
    saved: 'Salvato',
    conflict:
      'Qualcuno ha appena modificato questa impostazione. Abbiamo caricato le impostazioni più recenti.',
    saveError: 'Non è stato possibile salvare. Riprova.',
    adminOnly: 'Solo i proprietari e gli amministratori possono modificarlo.',
  },
  lt: {
    triggerLabel: 'Modelis ir kruopštumas: {model}, {effort}',
    title: 'Modelis ir kruopštumas',
    modelHeading: 'Modelis',
    effortHeading: 'Kruopštumas',
    autoBody: 'Rekomenduojamas daugumai darbų, visada atnaujintas.',
    opusBody: 'Pajėgiausias – sudėtingai analizei ir ilgiems tyrimams.',
    sonnetBody: 'Greitas ir pajėgus – kasdieniam darbui.',
    haikuBody: 'Greitas ir nebrangus, paprastoms užduotims.',
    solBody: 'Kruopštus OpenAI samprotavimas. Gali veikti už ES ribų.',
    otherBody: 'Kitas jūsų įmonės leidžiamas modelis.',
    costLower: 'Mažesnė kaina',
    costHigher: 'Didesnė kaina',
    fast: 'Greitas',
    balanced: 'Įprastas',
    thorough: 'Kruopštus',
    max: 'Maksimalus',
    fastBody: 'Greiti atsakymai į paprastus klausimus.',
    balancedBody: 'Galvoja tiek, kiek reikia kiekvienam klausimui.',
    thoroughBody: 'Skiria daugiau laiko samprotauti ir patikrinti savo darbą.',
    maxBody: 'Ilgiems tyrimams. Lėčiausias ir kruopščiausias.',
    unsupported: 'Nepasiekiama su {model}',
    maxNote:
      'Maksimaliai trunka ilgiau ir naudoja daugiau kredito. Kiekvienas vykdymas sustoja ties {cap}.',
    companyNote: 'Jūsų įmonė pasirenka, kurie modeliai pasiekiami.',
    manage: 'Tvarkyti modelius',
    settingsTitle: 'Modeliai',
    settingsIntro:
      'Pasirinkite modelius ir kruopštumo lygius, kuriuos gali naudoti {company} žmonės, ir kiek gali kainuoti vienas vykdymas.',
    loading: 'Įkeliami modelių nustatymai…',
    loadError: 'Nepavyko įkelti modelių nustatymų. Patikrinkite ryšį ir bandykite dar kartą.',
    unavailable: 'Modelių nustatymai čia dar nepasiekiami.',
    retry: 'Bandyti dar kartą',
    modelsHelp:
      'Visi gali rinktis iš šių bet kuriame pokalbyje. Nauji pokalbiai prasideda su numatytuoju.',
    defaultBadge: 'Numatytasis',
    makeDefault: 'Nustatyti numatytuoju',
    defaultLocked: 'Prieš išjungdami šį, pasirinkite kitą numatytąjį.',
    effortsHelp:
      'Didesnis kruopštumas duoda apgalvotesnius atsakymus, bet trunka ilgiau ir kainuoja daugiau.',
    capHeading: 'Kreditų riba vienam vykdymui',
    capHelp:
      'Vykdymas tvarkingai sustoja, kai sunaudoja tiek kreditų. Daugiausia sunaudoja ilgi tyrimai.',
    capField: 'Riba vienam vykdymui',
    capRange: 'Nuo {min} iki {max}.',
    capNow: 'Kiekvienas vykdymas sustoja ties {cap}.',
    capUnit: 'kreditų',
    capCredits: '{credits} kreditų',
    decision: 'Pakeitimai taikomi naujoms visų {company} žmonių žinutėms.',
    save: 'Išsaugoti',
    saving: 'Išsaugoma…',
    saved: 'Išsaugota',
    conflict: 'Kažkas ką tik tai pakeitė. Įkėlėme naujausius nustatymus.',
    saveError: 'Nepavyko išsaugoti. Bandykite dar kartą.',
    adminOnly: 'Tai gali keisti tik savininkai ir administratoriai.',
  },
  lv: {
    triggerLabel: 'Modelis un rūpība: {model}, {effort}',
    title: 'Modelis un rūpība',
    modelHeading: 'Modelis',
    effortHeading: 'Rūpība',
    autoBody: 'Ieteicams lielākajai daļai darba, vienmēr atjaunināts.',
    opusBody: 'Visspējīgākais — sarežģītai analīzei un ilgai izpētei.',
    sonnetBody: 'Ātrs un spējīgs — ikdienas darbam.',
    haikuBody: 'Ātrs un izdevīgs, vienkāršiem uzdevumiem.',
    solBody: 'Rūpīga spriešana no OpenAI. Var darboties ārpus ES.',
    otherBody: 'Cits modelis, ko atļauj jūsu uzņēmums.',
    costLower: 'Zemākas izmaksas',
    costHigher: 'Augstākas izmaksas',
    fast: 'Ātri',
    balanced: 'Līdzsvaroti',
    thorough: 'Rūpīgi',
    max: 'Maksimāli',
    fastBody: 'Ātras atbildes uz vienkāršiem jautājumiem.',
    balancedBody: 'Domā tik daudz, cik nepieciešams katram jautājumam.',
    thoroughBody: 'Velta vairāk laika spriešanai un sava darba pārbaudei.',
    maxBody: 'Ilgai izpētei. Vislēnākais un visrūpīgākais.',
    unsupported: 'Nav pieejams ar {model}',
    maxNote:
      'Maksimāli aizņem ilgāku laiku un izmanto vairāk kredīta. Katra izpilde apstājas pie {cap}.',
    companyNote: 'Jūsu uzņēmums izvēlas, kuri modeļi ir pieejami.',
    manage: 'Pārvaldīt modeļus',
    settingsTitle: 'Modeļi',
    settingsIntro:
      'Izvēlieties modeļus un rūpības līmeņus, ko {company} darbinieki var izmantot, un cik daudz drīkst maksāt viena izpilde.',
    loading: 'Ielādē modeļu iestatījumus…',
    loadError: 'Neizdevās ielādēt modeļu iestatījumus. Pārbaudiet savienojumu un mēģiniet vēlreiz.',
    unavailable: 'Modeļu iestatījumi šeit vēl nav pieejami.',
    retry: 'Mēģināt vēlreiz',
    modelsHelp:
      'Ikviens var izvēlēties no šiem jebkurā tērzēšanā. Jaunas tērzēšanas sākas ar noklusējumu.',
    defaultBadge: 'Noklusējums',
    makeDefault: 'Iestatīt kā noklusējumu',
    defaultLocked: 'Pirms izslēdzat šo, izvēlieties citu noklusējumu.',
    effortsHelp:
      'Lielāka rūpība dod pārdomātākas atbildes, taču aizņem ilgāku laiku un maksā vairāk.',
    capHeading: 'Kredītu limits vienai izpildei',
    capHelp:
      'Izpilde korekti apstājas, kad tā ir izmantojusi tik daudz kredītu. Visvairāk izmanto ilga izpēte.',
    capField: 'Ierobežojums vienai izpildei',
    capRange: 'No {min} līdz {max}.',
    capNow: 'Katra izpilde apstājas pie {cap}.',
    capUnit: 'kredīti',
    capCredits: '{credits} kredīti',
    decision: 'Izmaiņas attiecas uz visu {company} darbinieku jaunajām ziņām.',
    save: 'Saglabāt',
    saving: 'Saglabā…',
    saved: 'Saglabāts',
    conflict: 'Kāds tikko to mainīja. Mēs ielādējām jaunākos iestatījumus.',
    saveError: 'Neizdevās saglabāt. Mēģiniet vēlreiz.',
    adminOnly: 'To var mainīt tikai īpašnieki un administratori.',
  },
  mt: {
    triggerLabel: 'Mudell u reqqa: {model}, {effort}',
    title: 'Mudell u reqqa',
    modelHeading: 'Mudell',
    effortHeading: 'Reqqa',
    autoBody: 'Rakkomandat għal ħafna mix-xogħol, dejjem aġġornat.',
    opusBody: 'L-aktar kapaċi, għal analiżi kumplessa u riċerka twila.',
    sonnetBody: 'Mgħaġġel u kapaċi, għax-xogħol ta’ kuljum.',
    haikuBody: 'Mgħaġġel u bi prezz baxx, għal kompiti sempliċi.',
    solBody: 'Raġunament bir-reqqa minn OpenAI. Jista’ jaħdem barra l-UE.',
    otherBody: 'Mudell ieħor li tippermetti l-kumpanija tiegħek.',
    costLower: 'Spiża aktar baxxa',
    costHigher: 'Spiża ogħla',
    fast: 'Mgħaġġel',
    balanced: 'Bilanċjat',
    thorough: 'Bir-reqqa',
    max: 'Massimu',
    fastBody: 'Tweġibiet malajr għal mistoqsijiet sempliċi.',
    balancedBody: 'Jaħseb kemm teħtieġ kull mistoqsija.',
    thoroughBody: 'Jieħu aktar ħin biex jirraġuna u jiċċekkja x-xogħol tiegħu.',
    maxBody: 'Għal riċerka twila. L-aktar bil-mod u bl-akbar reqqa.',
    unsupported: 'Mhux disponibbli ma’ {model}',
    maxNote: 'Massimu jieħu aktar ħin u juża aktar kreditu. Kull run jieqaf f’{cap}.',
    companyNote: 'Il-kumpanija tiegħek tagħżel liema mudelli huma disponibbli.',
    manage: 'Immaniġġja l-mudelli',
    settingsTitle: 'Mudelli',
    settingsIntro:
      'Agħżel il-mudelli u l-livelli ta’ reqqa li n-nies f’{company} jistgħu jużaw, u kemm jista’ jiswa run wieħed.',
    loading: 'Qed jiġu mgħobbija l-issettjar tal-mudelli…',
    loadError:
      'Ma stajniex ngħabbu l-issettjar tal-mudelli. Iċċekkja l-konnessjoni u erġa’ pprova.',
    unavailable: 'L-issettjar tal-mudelli għadu mhux disponibbli hawn.',
    retry: 'Erġa’ pprova',
    modelsHelp:
      'Kulħadd jista’ jagħżel minn dawn f’kull chat. Chats ġodda jibdew bil-prestabbilit.',
    defaultBadge: 'Prestabbilit',
    makeDefault: 'Agħmlu prestabbilit',
    defaultLocked: 'Agħżel prestabbilit ieħor qabel ma titfi dan.',
    effortsHelp: 'Aktar reqqa tagħti tweġibiet aktar maħsuba, iżda tieħu aktar ħin u tiswa aktar.',
    capHeading: 'Limitu ta’ krediti għal kull tħaddim',
    capHelp:
      'Tħaddim jieqaf b’mod ordnat meta juża dawn il-krediti. Ir-riċerka twila tuża l-aktar.',
    capField: 'Limitu għal kull run',
    capRange: 'Minn {min} sa {max}.',
    capNow: 'Kull run jieqaf f’{cap}.',
    capUnit: 'krediti',
    capCredits: '{credits} krediti',
    decision: 'Il-bidliet japplikaw għal messaġġi ġodda ta’ kulħadd f’{company}.',
    save: 'Issejvja',
    saving: 'Qed jiġi ssejvjat…',
    saved: 'Issejvjat',
    conflict: 'Xi ħadd għadu kif biddel dan. Għabbejna l-aktar issettjar riċenti.',
    saveError: 'Ma stajniex nissejvjaw dan. Erġa’ pprova.',
    adminOnly: 'Is-sidien u l-amministraturi biss jistgħu jibdlu dan.',
  },
  nl: {
    triggerLabel: 'Model en grondigheid: {model}, {effort}',
    title: 'Model en grondigheid',
    modelHeading: 'Model',
    effortHeading: 'Grondigheid',
    autoBody: 'Aanbevolen voor het meeste werk, altijd actueel.',
    opusBody: 'Het krachtigst, voor complexe analyses en lang onderzoek.',
    sonnetBody: 'Snel en capabel, voor dagelijks werk.',
    haikuBody: 'Snel en voordelig, voor eenvoudige taken.',
    solBody: 'Zorgvuldig redeneren van OpenAI. Kan buiten de EU draaien.',
    otherBody: 'Een ander model dat je bedrijf toestaat.',
    costLower: 'Lagere kosten',
    costHigher: 'Hogere kosten',
    fast: 'Snel',
    balanced: 'Normaal',
    thorough: 'Grondig',
    max: 'Max',
    fastBody: 'Snelle antwoorden op eenvoudige vragen.',
    balancedBody: 'Denkt zoveel na als elke vraag nodig heeft.',
    thoroughBody: 'Neemt meer tijd om te redeneren en het werk te controleren.',
    maxBody: 'Voor lang onderzoek. Het traagst en het zorgvuldigst.',
    unsupported: 'Niet beschikbaar met {model}',
    maxNote: 'Max duurt langer en gebruikt meer tegoed. Elke run stopt bij {cap}.',
    companyNote: 'Je bedrijf bepaalt welke modellen beschikbaar zijn.',
    manage: 'Modellen beheren',
    settingsTitle: 'Modellen',
    settingsIntro:
      'Kies welke modellen en niveaus van grondigheid mensen bij {company} kunnen gebruiken, en hoeveel één run mag kosten.',
    loading: 'Modelinstellingen laden…',
    loadError:
      'We konden de modelinstellingen niet laden. Controleer je verbinding en probeer het opnieuw.',
    unavailable: 'Modelinstellingen zijn hier nog niet beschikbaar.',
    retry: 'Opnieuw proberen',
    modelsHelp: 'Iedereen kiest hieruit in elke chat. Nieuwe chats beginnen met de standaard.',
    defaultBadge: 'Standaard',
    makeDefault: 'Als standaard instellen',
    defaultLocked: 'Kies eerst een andere standaard voordat je deze uitzet.',
    effortsHelp: 'Meer grondigheid geeft zorgvuldigere antwoorden, maar duurt langer en kost meer.',
    capHeading: 'Creditlimiet per run',
    capHelp:
      'Een run stopt netjes wanneer die zoveel credits heeft gebruikt. Lang onderzoek gebruikt de meeste.',
    capField: 'Limiet per run',
    capRange: 'Van {min} tot {max}.',
    capNow: 'Elke run stopt bij {cap}.',
    capUnit: 'credits',
    capCredits: '{credits} credits',
    decision: 'Wijzigingen gelden voor nieuwe berichten van iedereen bij {company}.',
    save: 'Opslaan',
    saving: 'Opslaan…',
    saved: 'Opgeslagen',
    conflict: 'Iemand heeft dit net gewijzigd. We hebben de nieuwste instellingen geladen.',
    saveError: 'We konden dit niet opslaan. Probeer het opnieuw.',
    adminOnly: 'Alleen eigenaren en beheerders kunnen dit wijzigen.',
  },
  pl: {
    triggerLabel: 'Model i staranność: {model}, {effort}',
    title: 'Model i staranność',
    modelHeading: 'Model',
    effortHeading: 'Staranność',
    autoBody: 'Zalecany do większości pracy, zawsze aktualny.',
    opusBody: 'Najbardziej zaawansowany, do złożonych analiz i długich badań.',
    sonnetBody: 'Szybki i sprawny, do codziennej pracy.',
    haikuBody: 'Szybki i niedrogi, do prostych zadań.',
    solBody: 'Staranne rozumowanie od OpenAI. Może działać poza UE.',
    otherBody: 'Inny model dozwolony przez twoją firmę.',
    costLower: 'Niższy koszt',
    costHigher: 'Wyższy koszt',
    fast: 'Szybka',
    balanced: 'Średnia',
    thorough: 'Dokładna',
    max: 'Maksymalna',
    fastBody: 'Szybkie odpowiedzi na proste pytania.',
    balancedBody: 'Myśli tyle, ile wymaga każde pytanie.',
    thoroughBody: 'Poświęca więcej czasu na rozumowanie i sprawdzenie pracy.',
    maxBody: 'Do długich badań. Najwolniejsza i najstaranniejsza.',
    unsupported: 'Niedostępne dla modelu {model}',
    maxNote:
      'Tryb maksymalny trwa dłużej i zużywa więcej środków. Każde uruchomienie kończy się przy {cap}.',
    companyNote: 'Twoja firma wybiera, które modele są dostępne.',
    manage: 'Zarządzaj modelami',
    settingsTitle: 'Modele',
    settingsIntro:
      'Wybierz modele i poziomy staranności, z których mogą korzystać osoby w {company}, oraz ile może kosztować jedno uruchomienie.',
    loading: 'Wczytywanie ustawień modeli…',
    loadError: 'Nie udało się wczytać ustawień modeli. Sprawdź połączenie i spróbuj ponownie.',
    unavailable: 'Ustawienia modeli nie są tu jeszcze dostępne.',
    retry: 'Spróbuj ponownie',
    modelsHelp:
      'Każdy może wybierać spośród nich w dowolnym czacie. Nowe czaty zaczynają się od domyślnego.',
    defaultBadge: 'Domyślny',
    makeDefault: 'Ustaw jako domyślny',
    defaultLocked: 'Wybierz inną wartość domyślną, zanim to wyłączysz.',
    effortsHelp:
      'Większa staranność daje bardziej przemyślane odpowiedzi, ale trwa dłużej i kosztuje więcej.',
    capHeading: 'Limit kredytów na uruchomienie',
    capHelp:
      'Uruchomienie kończy się poprawnie, gdy zużyje tyle kredytów. Najwięcej zużywa długie badanie.',
    capField: 'Limit na uruchomienie',
    capRange: 'Od {min} do {max}.',
    capNow: 'Każde uruchomienie kończy się przy {cap}.',
    capUnit: 'kredytów',
    capCredits: '{credits} kredytów',
    decision: 'Zmiany dotyczą nowych wiadomości wszystkich osób w {company}.',
    save: 'Zapisz',
    saving: 'Zapisywanie…',
    saved: 'Zapisano',
    conflict: 'Ktoś właśnie to zmienił. Wczytaliśmy najnowsze ustawienia.',
    saveError: 'Nie udało się zapisać. Spróbuj ponownie.',
    adminOnly: 'Tylko właściciele i administratorzy mogą to zmienić.',
  },
  pt: {
    triggerLabel: 'Modelo e raciocínio: {model}, {effort}',
    title: 'Modelo e raciocínio',
    modelHeading: 'Modelo',
    effortHeading: 'Raciocínio',
    autoBody: 'Recomendado para a maioria do trabalho, sempre atualizado.',
    opusBody: 'O mais capaz, para análises complexas e pesquisas longas.',
    sonnetBody: 'Rápido e capaz, para o trabalho do dia a dia.',
    haikuBody: 'Rápido e económico, para tarefas simples.',
    solBody: 'Raciocínio cuidadoso da OpenAI. Pode ser executado fora da UE.',
    otherBody: 'Outro modelo permitido pela sua empresa.',
    costLower: 'Custo mais baixo',
    costHigher: 'Custo mais alto',
    fast: 'Rápido',
    balanced: 'Equilibrado',
    thorough: 'Aprofundado',
    max: 'Máximo',
    fastBody: 'Respostas rápidas a perguntas simples.',
    balancedBody: 'Pensa tanto quanto cada pergunta precisa.',
    thoroughBody: 'Demora mais a raciocinar e a verificar o trabalho.',
    maxBody: 'Para pesquisas longas. O mais lento e o mais minucioso.',
    unsupported: 'Não disponível com {model}',
    maxNote: 'Máximo demora mais e usa mais crédito. Cada execução para em {cap}.',
    companyNote: 'A sua empresa escolhe os modelos disponíveis.',
    manage: 'Gerir modelos',
    settingsTitle: 'Modelos',
    settingsIntro:
      'Escolha os modelos e os níveis de raciocínio que as pessoas da {company} podem usar, e quanto pode custar uma execução.',
    loading: 'A carregar as definições dos modelos…',
    loadError:
      'Não foi possível carregar as definições dos modelos. Verifique a ligação e tente novamente.',
    unavailable: 'As definições dos modelos ainda não estão disponíveis aqui.',
    retry: 'Tentar novamente',
    modelsHelp:
      'Todos escolhem entre estes em qualquer chat. Os chats novos começam com o predefinido.',
    defaultBadge: 'Predefinido',
    makeDefault: 'Tornar predefinido',
    defaultLocked: 'Escolha outro predefinido antes de desativar este.',
    effortsHelp: 'Mais raciocínio dá respostas mais cuidadosas, mas demora mais e custa mais.',
    capHeading: 'Limite de créditos por execução',
    capHelp:
      'Uma execução para de forma ordenada quando usa esta quantidade de créditos. As pesquisas longas são as que mais usam.',
    capField: 'Limite por execução',
    capRange: 'De {min} a {max}.',
    capNow: 'Cada execução para em {cap}.',
    capUnit: 'créditos',
    capCredits: '{credits} créditos',
    decision: 'As alterações aplicam-se às novas mensagens de todas as pessoas da {company}.',
    save: 'Guardar',
    saving: 'A guardar…',
    saved: 'Guardado',
    conflict: 'Alguém acabou de alterar isto. Carregámos as definições mais recentes.',
    saveError: 'Não foi possível guardar. Tente novamente.',
    adminOnly: 'Só proprietários e administradores podem alterar isto.',
  },
  ro: {
    triggerLabel: 'Model și profunzime: {model}, {effort}',
    title: 'Model și profunzime',
    modelHeading: 'Model',
    effortHeading: 'Profunzime',
    autoBody: 'Recomandat pentru majoritatea sarcinilor, mereu actualizat.',
    opusBody: 'Cel mai capabil, pentru analize complexe și cercetări lungi.',
    sonnetBody: 'Rapid și capabil, pentru munca de zi cu zi.',
    haikuBody: 'Rapid și accesibil, pentru sarcini simple.',
    solBody: 'Raționament atent de la OpenAI. Poate rula în afara UE.',
    otherBody: 'Alt model permis de compania ta.',
    costLower: 'Cost mai mic',
    costHigher: 'Cost mai mare',
    fast: 'Rapid',
    balanced: 'Echilibrat',
    thorough: 'Temeinic',
    max: 'Maxim',
    fastBody: 'Răspunsuri rapide la întrebări simple.',
    balancedBody: 'Gândește cât are nevoie fiecare întrebare.',
    thoroughBody: 'Își ia mai mult timp să raționeze și să-și verifice munca.',
    maxBody: 'Pentru cercetări lungi. Cel mai lent și mai atent.',
    unsupported: 'Indisponibil cu {model}',
    maxNote:
      'Maxim durează mai mult și folosește mai mult credit. Fiecare rulare se oprește la {cap}.',
    companyNote: 'Compania ta alege ce modele sunt disponibile.',
    manage: 'Gestionează modelele',
    settingsTitle: 'Modele',
    settingsIntro:
      'Alege modelele și nivelurile de profunzime pe care le pot folosi oamenii de la {company} și cât poate costa o rulare.',
    loading: 'Se încarcă setările modelelor…',
    loadError: 'Nu am putut încărca setările modelelor. Verifică conexiunea și încearcă din nou.',
    unavailable: 'Setările modelelor nu sunt încă disponibile aici.',
    retry: 'Încearcă din nou',
    modelsHelp:
      'Oricine poate alege dintre acestea în orice chat. Chaturile noi încep cu cel implicit.',
    defaultBadge: 'Implicit',
    makeDefault: 'Setează ca implicit',
    defaultLocked: 'Alege alt implicit înainte să dezactivezi acesta.',
    effortsHelp:
      'Mai multă profunzime oferă răspunsuri mai atente, dar durează mai mult și costă mai mult.',
    capHeading: 'Limită de credite per rulare',
    capHelp:
      'O rulare se oprește curat când a folosit atâtea credite. Cercetările lungi folosesc cele mai multe.',
    capField: 'Limită pe rulare',
    capRange: 'Între {min} și {max}.',
    capNow: 'Fiecare rulare se oprește la {cap}.',
    capUnit: 'credite',
    capCredits: '{credits} credite',
    decision: 'Modificările se aplică mesajelor noi ale tuturor celor de la {company}.',
    save: 'Salvează',
    saving: 'Se salvează…',
    saved: 'Salvat',
    conflict: 'Cineva tocmai a modificat asta. Am încărcat cele mai noi setări.',
    saveError: 'Nu am putut salva. Încearcă din nou.',
    adminOnly: 'Doar proprietarii și administratorii pot modifica asta.',
  },
  sk: {
    triggerLabel: 'Model a dôkladnosť: {model}, {effort}',
    title: 'Model a dôkladnosť',
    modelHeading: 'Model',
    effortHeading: 'Dôkladnosť',
    autoBody: 'Odporúčané pre väčšinu práce, vždy aktuálne.',
    opusBody: 'Najschopnejší, na zložité analýzy a dlhý výskum.',
    sonnetBody: 'Rýchly a schopný, na každodennú prácu.',
    haikuBody: 'Rýchly a cenovo dostupný, na jednoduché úlohy.',
    solBody: 'Starostlivé uvažovanie od OpenAI. Môže bežať mimo EÚ.',
    otherBody: 'Ďalší model, ktorý vaša firma povoľuje.',
    costLower: 'Nižšia cena',
    costHigher: 'Vyššia cena',
    fast: 'Rýchlo',
    balanced: 'Vyvážene',
    thorough: 'Dôkladne',
    max: 'Maximálne',
    fastBody: 'Rýchle odpovede na jednoduché otázky.',
    balancedBody: 'Premýšľa toľko, koľko každá otázka potrebuje.',
    thoroughBody: 'Venuje viac času uvažovaniu a kontrole svojej práce.',
    maxBody: 'Na dlhý výskum. Najpomalší a najstarostlivejší.',
    unsupported: 'S modelom {model} nie je k dispozícii',
    maxNote:
      'Maximálna dôkladnosť trvá dlhšie a spotrebuje viac kreditu. Každý beh sa zastaví na {cap}.',
    companyNote: 'Vaša firma vyberá, ktoré modely sú k dispozícii.',
    manage: 'Spravovať modely',
    settingsTitle: 'Modely',
    settingsIntro:
      'Vyberte modely a úrovne dôkladnosti, ktoré môžu ľudia vo firme {company} používať, a koľko smie stáť jeden beh.',
    loading: 'Načítavajú sa nastavenia modelov…',
    loadError:
      'Nastavenia modelov sa nepodarilo načítať. Skontrolujte pripojenie a skúste to znova.',
    unavailable: 'Nastavenia modelov tu zatiaľ nie sú k dispozícii.',
    retry: 'Skúsiť znova',
    modelsHelp:
      'Každý si z nich môže vybrať v ľubovoľnom chate. Nové chaty začínajú s predvoleným.',
    defaultBadge: 'Predvolené',
    makeDefault: 'Nastaviť ako predvolené',
    defaultLocked: 'Pred vypnutím zvoľte inú predvolenú možnosť.',
    effortsHelp:
      'Vyššia dôkladnosť prináša starostlivejšie odpovede, ale trvá dlhšie a stojí viac.',
    capHeading: 'Limit kreditov na jedno spustenie',
    capHelp: 'Spustenie sa korektne zastaví, keď minie toľko kreditov. Najviac minie dlhý výskum.',
    capField: 'Limit na jeden beh',
    capRange: 'Od {min} do {max}.',
    capNow: 'Každý beh sa zastaví na {cap}.',
    capUnit: 'kreditov',
    capCredits: '{credits} kreditov',
    decision: 'Zmeny platia pre nové správy všetkých ľudí vo firme {company}.',
    save: 'Uložiť',
    saving: 'Ukladá sa…',
    saved: 'Uložené',
    conflict: 'Niekto to práve zmenil. Načítali sme najnovšie nastavenia.',
    saveError: 'Uloženie sa nepodarilo. Skúste to znova.',
    adminOnly: 'Meniť to môžu iba vlastníci a správcovia.',
  },
  sl: {
    triggerLabel: 'Model in temeljitost: {model}, {effort}',
    title: 'Model in temeljitost',
    modelHeading: 'Model',
    effortHeading: 'Temeljitost',
    autoBody: 'Priporočeno za večino dela, vedno posodobljeno.',
    opusBody: 'Najzmogljivejši, za zahtevne analize in dolge raziskave.',
    sonnetBody: 'Hiter in zmogljiv, za vsakodnevno delo.',
    haikuBody: 'Hiter in cenovno ugoden, za preproste naloge.',
    solBody: 'Skrbno sklepanje podjetja OpenAI. Lahko deluje zunaj EU.',
    otherBody: 'Drug model, ki ga dovoljuje vaše podjetje.',
    costLower: 'Nižja cena',
    costHigher: 'Višja cena',
    fast: 'Hitro',
    balanced: 'Zmerno',
    thorough: 'Temeljito',
    max: 'Največ',
    fastBody: 'Hitri odgovori na preprosta vprašanja.',
    balancedBody: 'Razmisli toliko, kolikor zahteva vsako vprašanje.',
    thoroughBody: 'Vzame si več časa za sklepanje in preverjanje dela.',
    maxBody: 'Za dolge raziskave. Najpočasnejše in najskrbnejše.',
    unsupported: 'Ni na voljo z modelom {model}',
    maxNote:
      'Največja temeljitost traja dlje in porabi več dobroimetja. Vsak zagon se ustavi pri {cap}.',
    companyNote: 'Vaše podjetje izbere, kateri modeli so na voljo.',
    manage: 'Upravljaj modele',
    settingsTitle: 'Modeli',
    settingsIntro:
      'Izberite modele in ravni temeljitosti, ki jih lahko uporabljajo ljudje v podjetju {company}, in koliko sme stati en zagon.',
    loading: 'Nalaganje nastavitev modelov…',
    loadError: 'Nastavitev modelov ni bilo mogoče naložiti. Preverite povezavo in poskusite znova.',
    unavailable: 'Nastavitve modelov tukaj še niso na voljo.',
    retry: 'Poskusi znova',
    modelsHelp:
      'Vsakdo lahko izbira med njimi v katerem koli klepetu. Novi klepeti se začnejo s privzetim.',
    defaultBadge: 'Privzeto',
    makeDefault: 'Nastavi kot privzeto',
    defaultLocked: 'Preden to izklopite, izberite drugo privzeto možnost.',
    effortsHelp: 'Večja temeljitost daje skrbnejše odgovore, vendar traja dlje in stane več.',
    capHeading: 'Omejitev kreditov na zagon',
    capHelp:
      'Zagon se urejeno ustavi, ko porabi toliko kreditov. Največ porabi dolgo raziskovanje.',
    capField: 'Omejitev na zagon',
    capRange: 'Od {min} do {max}.',
    capNow: 'Vsak zagon se ustavi pri {cap}.',
    capUnit: 'kreditov',
    capCredits: '{credits} kreditov',
    decision: 'Spremembe veljajo za nova sporočila vseh ljudi v podjetju {company}.',
    save: 'Shrani',
    saving: 'Shranjevanje…',
    saved: 'Shranjeno',
    conflict: 'Nekdo je to pravkar spremenil. Naložili smo najnovejše nastavitve.',
    saveError: 'Shranjevanje ni uspelo. Poskusite znova.',
    adminOnly: 'To lahko spremenijo samo lastniki in skrbniki.',
  },
  sv: {
    triggerLabel: 'Modell och noggrannhet: {model}, {effort}',
    title: 'Modell och noggrannhet',
    modelHeading: 'Modell',
    effortHeading: 'Noggrannhet',
    autoBody: 'Rekommenderas för det mesta, hålls uppdaterad.',
    opusBody: 'Mest kapabel, för komplex analys och lång research.',
    sonnetBody: 'Snabb och kapabel, för vardagligt arbete.',
    haikuBody: 'Snabb och prisvärd, för enkla uppgifter.',
    solBody: 'Noggrant resonemang från OpenAI. Kan köras utanför EU.',
    otherBody: 'En annan modell som ditt företag tillåter.',
    costLower: 'Lägre kostnad',
    costHigher: 'Högre kostnad',
    fast: 'Snabb',
    balanced: 'Balanserad',
    thorough: 'Grundlig',
    max: 'Max',
    fastBody: 'Snabba svar på enkla frågor.',
    balancedBody: 'Tänker så mycket som varje fråga kräver.',
    thoroughBody: 'Tar mer tid att resonera och kontrollera sitt arbete.',
    maxBody: 'För lång research. Långsammast och mest noggrann.',
    unsupported: 'Inte tillgänglig med {model}',
    maxNote: 'Max tar längre tid och använder mer kredit. Varje körning stoppas vid {cap}.',
    companyNote: 'Ditt företag väljer vilka modeller som finns.',
    manage: 'Hantera modeller',
    settingsTitle: 'Modeller',
    settingsIntro:
      'Välj vilka modeller och nivåer av noggrannhet personer på {company} kan använda, och hur mycket en körning får kosta.',
    loading: 'Läser in modellinställningar…',
    loadError:
      'Vi kunde inte läsa in modellinställningarna. Kontrollera anslutningen och försök igen.',
    unavailable: 'Modellinställningar finns inte här än.',
    retry: 'Försök igen',
    modelsHelp:
      'Alla kan välja bland dessa i vilken chatt som helst. Nya chattar börjar med standardvalet.',
    defaultBadge: 'Standard',
    makeDefault: 'Gör till standard',
    defaultLocked: 'Välj en annan standard innan du stänger av den här.',
    effortsHelp: 'Mer noggrannhet ger mer genomtänkta svar, men tar längre tid och kostar mer.',
    capHeading: 'Kreditgräns per körning',
    capHelp:
      'En körning stoppas snyggt när den har använt så många krediter. Lång research använder mest.',
    capField: 'Gräns per körning',
    capRange: 'Från {min} till {max}.',
    capNow: 'Varje körning stoppas vid {cap}.',
    capUnit: 'krediter',
    capCredits: '{credits} krediter',
    decision: 'Ändringarna gäller nya meddelanden för alla på {company}.',
    save: 'Spara',
    saving: 'Sparar…',
    saved: 'Sparat',
    conflict: 'Någon ändrade detta nyss. Vi har läst in de senaste inställningarna.',
    saveError: 'Vi kunde inte spara. Försök igen.',
    adminOnly: 'Bara ägare och administratörer kan ändra detta.',
  },
};

export default modelPickerCopy;
